import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import { handleOAuthCallback } from '../../redux/thunks/authThunks';

const OAuthCallback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        
        if (!token) {
          setError('No authentication token received');
          return;
        }
        
        await dispatch(handleOAuthCallback(token)).unwrap();

        // Deep-link restoration: ProtectedRoute saves the user's intended URL
        // to sessionStorage when it bounces them here. Pop it and go there,
        // falling back to /scripts (the canonical home) for a bare /login flow.
        let returnTo = '/scripts';
        try {
          const saved = sessionStorage.getItem('automatrix.returnTo');
          if (saved) {
            sessionStorage.removeItem('automatrix.returnTo');
            if (!saved.startsWith('/login') && !saved.startsWith('/auth/')) {
              returnTo = saved;
            }
          }
        } catch (e) { /* sessionStorage blocked — use fallback */ }
        navigate(returnTo, { replace: true });
      } catch (err) {
        setError(err.message || 'Authentication failed');
      }
    };
    
    processOAuthCallback();
  }, [dispatch, location, navigate]);
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
          {error}
        </Alert>
      ) : (
        <>
          <CircularProgress size={60} sx={{ mb: 4 }} />
          <Typography variant="h6" gutterBottom>
            Completing authentication...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we sign you in
          </Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthCallback;
